// src/views/Settings/org/OrgAccountsSection.jsx
//
// Las cuentas que la organización posee, y donde se crean.
//
// Vive aquí y no en configuración porque crear una cuenta no es una operación
// *de* una cuenta: es de la organización que la va a contener. En la base de
// datos, `accounts.organization_id` apunta hacia arriba — la organización es la
// dueña, y `POST /accounts` con `type=organization` exige justamente ese id.
import React, { useCallback, useEffect, useState } from 'react';
import { Wallet, Plus, Check } from 'lucide-react';
import GlassPanel from '../../../components/ui/GlassPanel';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import Spinner from '../../../components/Spinner';
import AccountService from '../../../services/account.service';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { PRIMARY, BUTTON_SIZE } from '../../../lib/buttonStyles';

export default function OrgAccountsSection({ organization, canManage }) {
  const { currentAccount } = useOrganization();
  const [accounts, setAccounts] = useState([]);
  // `settled` y no `loading`: lo que necesita el render es saber si ya hay
  // respuesta. Una lista vacía antes de la primera respuesta no es una lista
  // vacía, y afirmar que no hay cuentas sería mentir.
  const [settled, setSettled] = useState(false);
  const [error, setError] = useState(null);

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '' });
  const [busy, setBusy] = useState(false);

  const orgId = organization?.id;

  const load = useCallback(async () => {
    if (!orgId) return;
    const result = await AccountService.listAccounts({ organization_id: orgId, limit: 100 });
    if (result.success) setAccounts(result.data || []);
    else setError(result.error?.join?.(', ') || 'Failed to load accounts');
    setSettled(true);
  }, [orgId]);

  useEffect(() => {
    setSettled(false);
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!draft.name.trim() || busy) return;
    setBusy(true);
    setError(null);
    const result = await AccountService.createAccount({
      name: draft.name.trim(),
      description: draft.description.trim() || undefined,
      organizationId: orgId,
    });
    if (result.success) {
      setDraft({ name: '', description: '' });
      setCreating(false);
      await load();
    } else {
      setError(result.error?.join?.(', ') || 'Failed to create account');
    }
    setBusy(false);
  };

  if (!organization) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Accounts</h1>
          <p className="text-sm text-gray-400">
            Each account has its own balance, credentials and projects.
          </p>
        </div>
        {canManage && !creating && (
          <Button onClick={() => setCreating(true)} className={PRIMARY}>
            <Plus className="mr-2 h-4 w-4" />
            New account
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {creating && (
        <GlassPanel className="p-6">
          <h2 className="mb-4 text-lg font-semibold text-white">New account</h2>
          <div className="space-y-3">
            <Input
              autoFocus
              placeholder="Account name"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              className="border-white/20 bg-black/40 text-white"
            />
            <Textarea
              placeholder="What is it for? (optional)"
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              className="border-white/20 bg-black/40 text-white"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleCreate}
                disabled={!draft.name.trim() || busy}
                className={PRIMARY}
              >
                Create
              </Button>
              <Button
                variant="outline"
                onClick={() => { setCreating(false); setDraft({ name: '', description: '' }); }}
                className="border-white/20 bg-black/40 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
            </div>
          </div>
        </GlassPanel>
      )}

      {!settled ? (
        <div className="flex justify-center py-12">
          <Spinner size="md" color="purple" label="Loading accounts…" />
        </div>
      ) : accounts.length === 0 ? (
        <GlassPanel className="p-12 text-center">
          <Wallet className="mx-auto mb-4 h-12 w-12 text-gray-600" />
          <h3 className="mb-1 text-lg font-semibold text-white">No accounts yet</h3>
          <p className="text-sm text-gray-400">
            Create one to give this organization somewhere to hold its work.
          </p>
        </GlassPanel>
      ) : (
        <div className="space-y-3">
          {accounts.map((account) => {
            const isCurrent = account.id === currentAccount?.id;
            return (
              <GlassPanel key={account.id} className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-white">{account.name}</p>
                    <p className="truncate text-sm text-gray-400">
                      {account.description || 'No description'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge
                      className={
                        account.is_active === false
                          ? 'border-red-500/30 bg-red-500/20 text-red-400'
                          : 'border-green-500/30 bg-green-500/20 text-green-400'
                      }
                    >
                      {account.is_active === false ? 'INACTIVE' : 'ACTIVE'}
                    </Badge>
                    {/* La cuenta activa se marca, pero no se ofrece entrar a las
                        otras: el switcher todavía elige organización y deriva su
                        cuenta, así que un enlace a otra devolvería a esta. Una
                        opción que no funciona es peor que no tenerla. */}
                    {isCurrent && (
                      <span className="flex items-center gap-1.5 text-sm text-purple-300">
                        <Check className="h-4 w-4" />
                        Current
                      </span>
                    )}
                  </div>
                </div>
              </GlassPanel>
            );
          })}
        </div>
      )}
    </div>
  );
}
